import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import SimpleLoader from '../components/SimpleLoader';
import { getAllLedgerEntries } from '../services/ledger';
import toast from 'react-hot-toast';
import { PDFViewer } from '@react-pdf/renderer';
import CashRegisterPdf from '../components/pdf/CashRegisterPdf';
import CashRegisterPdfButton from '../components/pdf/CashRegisterPdfButton';
import Pagination from '../components/Pagination';
import TableContainer from '../components/TableContainer';

const CashRegister = () => {
    const navigate = useNavigate();
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [limit, setLimit] = useState(50);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortBy, setSortBy] = useState('date');
    const [sortOrder, setSortOrder] = useState('desc');
    const [total, setTotal] = useState(0);
    const [showPdfPreview, setShowPdfPreview] = useState(false);
    const [previewKey, setPreviewKey] = useState(0);

    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    // New Filter States
    const [paymentMode, setPaymentMode] = useState('');
    const [entryType, setEntryType] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        const fetchEntries = async () => {
            try {
                setShowPdfPreview(false)
                setLoading(true);
                const offset = (currentPage - 1) * limit;
                const { entries: fetchedEntries, count } = await getAllLedgerEntries({
                    limit,
                    offset,
                    sortBy,
                    sortOrder,
                    search: debouncedSearchTerm,
                    paymentMode,
                    entryType,
                    startDate,
                    endDate
                });
                setEntries(fetchedEntries);
                setTotal(count);
                setPreviewKey(prev => prev + 1); // Force PDF preview to refresh
            } catch (error) {
                toast.error(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchEntries();
    }, [limit, currentPage, sortBy, sortOrder, debouncedSearchTerm, paymentMode, entryType, startDate, endDate]);

    const handleLimitChange = (e) => {
        setLimit(parseInt(e.target.value, 10));
        setCurrentPage(1);
    };

    const handleSortByChange = (e) => {
        const [newSortBy, newSortOrder] = e.target.value.split(':');
        setSortBy(newSortBy);
        setSortOrder(newSortOrder);
    };

    const handlePaymentModeChange = (e) => {
        setPaymentMode(e.target.value);
        setCurrentPage(1);
    };

    const handleEntryTypeChange = (e) => {
        setEntryType(e.target.value);
        setCurrentPage(1);
    };

    const handleStartDateChange = (e) => {
        setStartDate(e.target.value);
        setCurrentPage(1);
    };

    const handleEndDateChange = (e) => {
        setEndDate(e.target.value);
        setCurrentPage(1);
    };

    // const totalPages = Math.ceil(total / limit);

    return (
        <DashboardLayout pageTitle="Cash Register">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4">
                <div className="flex items-center flex-wrap gap-2">
                    <select
                        value={limit}
                        onChange={handleLimitChange}
                        className="dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md p-2"
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                    <select
                        value={`${sortBy}:${sortOrder}`}
                        onChange={handleSortByChange}
                        className="dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md p-2"
                    >
                        <option value="date:desc">Date (Newest First)</option>
                        <option value="date:asc">Date (Oldest First)</option>
                        <option value="created_at:desc">Created (Newest First)</option>
                        <option value="created_at:asc">Created (Oldest First)</option>
                    </select>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md shadow text-sm"
                    >
                        Back
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowPdfPreview(!showPdfPreview)}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md shadow text-sm"
                    >
                        {showPdfPreview ? 'Hide PDF Preview' : 'Show PDF Preview'}
                    </button>
                    <CashRegisterPdfButton data={entries} key={previewKey} />
                </div>
            </div>

            <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {/* Search Filter */}
                <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search</label>
                    <input
                        type="text"
                        placeholder="Description, reference, name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                    />
                </div>
                
                {/* Payment Mode Filter */}
                <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Payment Mode</label>
                    <select
                        value={paymentMode}
                        onChange={handlePaymentModeChange}
                        className="dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md p-2 w-full"
                    >
                        <option value="">All Payment Modes</option>
                        <option value="Cash">Cash</option>
                        <option value="Bank Transfer">Bank Transfer</option>
                        <option value="Card">Card</option>
                        <option value="Cheque">Cheque</option>
                    </select>
                </div>

                {/* Entry Type Filter */}
                <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Entry Type</label>
                    <select
                        value={entryType}
                        onChange={handleEntryTypeChange}
                        className="dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md p-2 w-full"
                    >
                        <option value="all">All Types</option>
                        <option value="in">In (Credit)</option>
                        <option value="out">Out (Debit)</option>
                    </select>
                </div>

{/* Date Range Filters - Responsive layout */}
                <div className="flex flex-col justify-end sm:col-span-2 lg:col-span-1">
                    <div className="flex flex-col sm:flex-row gap-2">
                        <div className="flex flex-col flex-1 sm:flex-1">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={handleStartDateChange}
                                className="dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md p-2 w-full"
                            />
                        </div>
                        <div className="flex flex-col flex-1 sm:flex-1">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={handleEndDateChange}
                                className="dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md p-2 w-full"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className='mb-4'>
                {showPdfPreview && entries && (
                    <div style={{ height: '80vh' }} className="mb-4 text-right">
                        <PDFViewer width="100%" height="100%">
                            <CashRegisterPdf data={entries} key={previewKey} />
                        </PDFViewer>
                    </div>
                )}
            </div>

            {/* Data Table */}
            <TableContainer>
                {loading ? (
                    <SimpleLoader />
                ) : (
                    <>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold dark:text-white">
                                Cash Register Entries ({total})
                            </h3>
                            {entries.length > 0 && (
                                <span className="text-sm text-gray-500 dark:text-gray-400">
                                    Showing {entries.length} entries
                                </span>
                            )}
                        </div>

                        <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                                <tr>
                                    <th scope="col" className="px-6 py-3">Date</th>
                                    <th scope="col" className="px-6 py-3">Client</th>
                                    <th scope="col" className="px-6 py-3">Type</th>
                                    <th scope="col" className="px-6 py-3">Reference</th>
                                    <th scope="col" className="px-6 py-3">Description</th>
                                    <th scope="col" className="px-6 py-3 text-right">Debit (Out)</th>
                                    <th scope="col" className="px-6 py-3 text-right">Credit (In)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {entries.length > 0 ? (
                                    entries.map((entry, index) => (
                                        <tr
                                            key={`${entry.type}-${entry.id || index}`}
                                            className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {entry.date ? new Date(entry.date).toLocaleDateString() : 'N/A'}
                                            </td>
                                            <td className="px-6 py-4">
                                                {entry.file_opening_id ? (
                                                    <Link
                                                        to={`/file-opening/${entry.file_opening_id}`}
                                                        className="hover:underline text-blue-600 dark:text-blue-400"
                                                    >
                                                        {entry.name || 'Unknown Client'}
                                                    </Link>
                                                ) : entry.name || 'N/A'}
                                            </td>
                                            <td className="px-6 py-4 uppercase text-xs font-bold">
                                                <span className={`px-2 py-1 rounded ${entry.type === 'invoice'
                                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                                    : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                    }`}>
                                                    {entry.type || 'N/A'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                {entry.reference || 'N/A'}
                                            </td>
                                            <td className="px-6 py-4">
                                                {entry.description || 'N/A'}
                                            </td>
                                            <td className="px-6 py-4 text-right font-medium text-gray-900 dark:text-white">
                                                {parseFloat(entry.debit) > 0
                                                    ? `£${parseFloat(entry.debit).toFixed(2)}`
                                                    : '-'}
                                            </td>
                                            <td className="px-6 py-4 text-right font-medium text-green-600 dark:text-green-400">
                                                {parseFloat(entry.credit) > 0
                                                    ? `£${parseFloat(entry.credit).toFixed(2)}`
                                                    : '-'}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-8 text-center">
                                            <div className="text-gray-500 dark:text-gray-400">
                                                <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.801 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.801 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
                                                </svg>
                                                <p className="text-lg font-medium">No entries found</p>
                                                <p className="mt-2">Try adjusting your search or filters</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        {/* Pagination */}
                        <Pagination
                            currentPage={currentPage}
                            total={total}
                            limit={limit}
                            onPageChange={setCurrentPage}
                        />
                    </>
                )}
            </TableContainer>
        </DashboardLayout>
    );
};

export default CashRegister;